import React, { useEffect, useState, useMemo } from "react";
import axios from "../../axios";
import apiError from "../../error";
import CustomChatTable from "./custom-chat-table";
import McpHtmlMessage from "./mcp-html-message";
import {
    messageContainsHtmlTable,
    messageContainsHtmlChart,
    messageLooksLikeAiChart,
} from "./ai-html-utils";

/** Matches `CRP_` + 10 alphanumeric chars (e.g. server `CRP_${randomstring.generate(10)}`). */
const CRP_PREVIEW_PATTERN = /CRP_[A-Za-z0-9]{10}/;

export function extractCrpPreviewId(content) {
    if (typeof content !== "string") return null;
    const m = content.match(CRP_PREVIEW_PATTERN);
    return m ? m[0] : null;
}

export { messageContainsHtmlTable } from "./ai-html-utils";

/** True when the AI bubble should span the full chat column (wide tables / charts / CRP). */
export function aiMessageShouldUseFullWidth(html) {
    return (
        messageContainsHtmlTable(html) ||
        messageContainsHtmlChart(html) ||
        messageLooksLikeAiChart(html) ||
        !!extractCrpPreviewId(html)
    );
}

export default function AiChatMessageBody({ content, isStreaming }) {
    const previewId = useMemo(() => extractCrpPreviewId(content), [content]);

    const [previewState, setPreviewState] = useState({
        loading: false,
        data: null,
        error: false,
        status: "draft",
        customReportId: null,
        customReport: {}
    });

    useEffect(() => {
        if (!previewId || isStreaming) {
            if (!previewId) {
                setPreviewState({ loading: false, data: null, error: false });
            }
            return undefined;
        }

        let cancelled = false;
        setPreviewState({ loading: true, data: null, error: false });

        axios
            .get(`/api/custom-reports/dynamic/${encodeURIComponent(previewId)}?limit=10`)
            .then((res) => {
                if (cancelled) return;
                const d = res.data || {};
                setPreviewState({
                    loading: false,
                    data: {
                        reportList: Array.isArray(d.reportList) ? d.reportList : [],
                        totalCount: typeof d.totalCount === "number" ? d.totalCount : 0,
                        headers: Array.isArray(d.headers) ? d.headers : [],
                    },
                    error: false,
                    status: d.status,
                    customReportId: d.customReportId,
                    customReport: d.customReport
                });
            })
            .catch((err) => {
                if (cancelled) return;
                apiError(err);
                setPreviewState({ loading: false, data: null, error: true });
            });

        return () => { cancelled = true; };
    }, [previewId, isStreaming]);

    if (!previewId) {
        return (
            <>
                <McpHtmlMessage html={content} isStreaming={isStreaming} />
                {isStreaming && (
                    <div className="text-muted small py-1" style={{ fontSize: "12px" }}>
                        Completing response…
                    </div>
                )}
            </>
        );
    }

    return (
        <div className="mcp-ai-message-body">
            {isStreaming && (
                <div className="text-muted small py-2" style={{ fontSize: "12px" }}>
                    Completing response…
                </div>
            )}
            {!isStreaming && previewState.loading && (
                <div className="text-muted small py-2" style={{ fontSize: "12px" }}>
                    Loading report preview…
                </div>
            )}
            {!isStreaming && !previewState.loading && previewState.data && (
                <CustomChatTable
                    previewId={previewId}
                    headers={previewState.data.headers}
                    reportList={previewState.data.reportList}
                    totalCount={previewState.data.totalCount}
                    status={previewState.status}
                    customReportId={previewState.customReportId}
                    customReport={previewState.customReport}
                />
            )}
            {!isStreaming && !previewState.loading && previewState.error && (
                <div className="text-danger small py-2">Could not load report preview.</div>
            )}
        </div>
    );
}
